/* ═══════════════════════════════════════════════════════════════
   InvoiceService — Invoice CRUD via EC2 API.

   Tracking-only model: we store the invoice + line items + lifecycle
   dates + payment amounts.  No payment processor, no bank rails — that
   stays in QuickBooks or wherever the firm does actual money movement.

   Invoice numbering: simple incrementing pattern INV-YYYY-NNNN (per
   firm).  We compute the next number client-side from the existing
   list — fine until two PMs hit "Create" within the same render tick;
   the rare collision is recoverable (server has no UNIQUE constraint
   on invoice_number, so the worst case is a duplicate string the user
   can edit).
   ═══════════════════════════════════════════════════════════════ */

import type { Invoice, InvoiceLineItem, HourEntry } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import { log as logActivity } from './activityLogger';

const invoiceOpts: TableOpts = {
  jsonFields: ['lineItems'],
  columnMap: { line_items: 'line_items_json' },
};

/** Compute the next invoice number for a firm based on existing rows.
 *  Pattern: INV-YYYY-NNNN where NNNN is the highest existing N+1 for the
 *  current year, or 0001 when no invoices exist this year. */
function nextInvoiceNumber(existing: Invoice[]): string {
  const year = new Date().getFullYear();
  const prefix = `INV-${year}-`;
  let max = 0;
  for (const inv of existing) {
    if (!inv.invoiceNumber || !inv.invoiceNumber.startsWith(prefix)) continue;
    const tail = inv.invoiceNumber.slice(prefix.length);
    const n = parseInt(tail, 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return `${prefix}${String(max + 1).padStart(4, '0')}`;
}

export const InvoiceService = {
  list: async (projectId?: string): Promise<Invoice[]> => {
    if (projectId) return cache.query<Invoice>('invoices', { projectId }, invoiceOpts);
    return cache.list<Invoice>('invoices', invoiceOpts);
  },

  get: async (id: string): Promise<Invoice | null> => {
    const all = await InvoiceService.list();
    return all.find((i) => i.id === id) || null;
  },

  save: async (inv: Invoice): Promise<Invoice> => {
    const isNew = !inv.id;
    const prevStatus = !isNew ? (await InvoiceService.get(inv.id))?.status : null;
    const saved = await cache.save('invoices', inv, invoiceOpts);
    // Differentiate status changes (sent / paid) from plain saves so the
    // activity log surfaces business-meaningful events not just edits.
    let action = isNew ? 'invoice.create' : 'invoice.update';
    if (!isNew && prevStatus !== saved.status) {
      if (saved.status === 'sent') action = 'invoice.send';
      else if (saved.status === 'paid') action = 'invoice.paid';
      else if (saved.status === 'void') action = 'invoice.void';
    }
    logActivity({
      action,
      entityType: 'invoice',
      entityId: saved.id,
      metadata: {
        invoiceNumber: saved.invoiceNumber,
        status: saved.status,
        total: saved.total,
        projectId: saved.projectId,
      },
    });
    return saved;
  },

  delete: async (id: string): Promise<void> => {
    await cache.remove('invoices', id);
    logActivity({ action: 'invoice.delete', entityType: 'invoice', entityId: id });
  },

  /** Allocate the next invoice number for the current firm. */
  nextInvoiceNumber: async (): Promise<string> => {
    const all = await InvoiceService.list();
    return nextInvoiceNumber(all);
  },

  /** Build line items from a list of hour entries.  Groups by project +
   *  activity code, sums hours, and emits one line item per group with
   *  source_kind='time' + source_ids = the consumed entry IDs.  Caller
   *  supplies a rate-resolver that returns the $/hr for each entry
   *  (typically billing-role default rate or the team-member override). */
  lineItemsFromTimeEntries: (
    entries: HourEntry[],
    rateFor: (entry: HourEntry) => number,
    descFor: (entry: HourEntry) => string
  ): InvoiceLineItem[] => {
    // Group by descFor's output so identical rate × description rows merge.
    const groups = new Map<
      string,
      { description: string; rate: number; hours: number; ids: string[] }
    >();
    for (const e of entries) {
      const rate = rateFor(e) || 0;
      const desc = descFor(e);
      const key = `${desc}|${rate}`;
      const g = groups.get(key) || { description: desc, rate, hours: 0, ids: [] };
      g.hours += Number(e.hours) || 0;
      g.ids.push(e.id);
      groups.set(key, g);
    }
    return Array.from(groups.values()).map((g) => ({
      description: g.description,
      // Schema column is `hours` (numeric); we mirror to `quantity` for
      // non-time line items in the editor.
      hours: Math.round(g.hours * 100) / 100,
      quantity: Math.round(g.hours * 100) / 100,
      rate: g.rate,
      amount: Math.round(g.hours * g.rate * 100) / 100,
      sourceKind: 'time' as const,
      sourceIds: g.ids,
    }));
  },

  /** Recompute subtotal + tax + total from line items. */
  recompute: (inv: Invoice): Invoice => {
    const subtotal = (inv.lineItems || []).reduce((s, li) => s + (li.amount || 0), 0);
    const taxRate = (inv as any).taxRate ?? 0;
    const tax = Math.round(subtotal * (taxRate / 100) * 100) / 100;
    const total = Math.round((subtotal + tax) * 100) / 100;
    return { ...inv, subtotal, tax, total };
  },

  /** AR aging buckets across all firm invoices. */
  ageingBuckets: (invoices: Invoice[]) => {
    const today = new Date();
    const out = { current: 0, d30: 0, d60: 0, d90: 0 };
    for (const inv of invoices) {
      const status = (inv.status || '') as string;
      if (!status || status === 'draft' || status === 'paid' || status === 'void') continue;
      const issued = (inv as any).issueDate || (inv as any).date;
      if (!issued) continue;
      const days = Math.floor((today.getTime() - new Date(issued).getTime()) / 86400000);
      const open = (inv.total || 0) - ((inv as any).amountPaid || 0);
      if (open <= 0) continue;
      if (days <= 30) out.current += open;
      else if (days <= 60) out.d30 += open;
      else if (days <= 90) out.d60 += open;
      else out.d90 += open;
    }
    return out;
  },
};
